import { Icon } from '@iconify/react';
import { Box, Stack, TableCell, TableRow, Typography } from '@mui/material';
import { format } from 'date-fns';
import { he } from 'date-fns/locale';
import { useSnackbar } from 'notistack';
import React, { useState } from 'react';
import ReactLoading from 'react-loading';
import { useNavigate } from 'react-router-dom';
import { Schema } from '../../../../../../amplify/data/resource';
import awsConfig from '../../../../../amplify_outputs.json';
import { FundLogo } from '../../../../../components/custom/FundLogo';

import StatusTextCircle from '../../../../../components/custom/StatusTextCircle';
import ArrowIcon from '../../../../../components/icons/ArrowIcon';

import Sidebar from '../../../AgentDashboard/PaymentList/Sidebar';

// @ts-ignore
type File = Schema['File']['type'];
// @ts-ignore
type Employee = Schema['Employee']['type'];

interface FileItemProps {
  file: File;
}

const getFileIcon = (status: String) => {
  switch (status) {
    case 'COMPLETED':
      return '/assets/dashboard/fileGreen.svg';
    case 'IN_PROGRESS':
      return '/assets/dashboard/fileYellow.svg';
    case 'PENDING':
      return '/assets/dashboard/fileGrey.svg';
    case 'REJECTED':
      return '/assets/dashboard/fileRed.svg';
    default:
      return '/assets/dashboard/fileGrey.svg';
  }
};

const getFileIconTextColor = (status: String) => {
  switch (status) {
    case 'COMPLETED':
      return '#94C729';
    case 'IN_PROGRESS':
      return '#F6B209';
    case 'PENDING':
      return '#808080';
    case 'REJECTED':
      return '#FF6F78';
    default:
      return '#808080';
  }
};

const FileItem: React.FC<FileItemProps> = ({ file }) => {
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  const [openDetails, setOpenDetails] = useState(false);

  const handleToggleDetails = () => {
    setOpenDetails((prev) => !prev);
  };

  const totalToBePaid = Array.isArray(file?.deposits)
    ? file.deposits.reduce(
        (accumulator: number, deposit: { totalDepositAmount?: number }) => {
          return accumulator + (deposit.totalDepositAmount || 0);
        },
        0,
      )
    : 0;

  const handleDownload = async (e: any) => {
    e.stopPropagation();
    if (file) {
      const bucketName = awsConfig.storage.bucket_name;
      const fileKey = `files/${file.id}`;
      const s3Url = `https://${bucketName}.s3.amazonaws.com/${fileKey}`;

      try {
        const response = await fetch(s3Url);
        if (!response.ok) {
          throw new Error(`שגיאה בהורדת הקובץ: ${response.statusText}`);
        }

        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = 'file';
        a.click();
        window.URL.revokeObjectURL(url);

        enqueueSnackbar('הקובץ ירד בהצלחה!', { variant: 'success' });
      } catch {
        enqueueSnackbar('שגיאה בהורדת הקובץ', { variant: 'error' });
      }
    } else {
      enqueueSnackbar('לא ניתן להוריד את הקובץ', { variant: 'error' });
    }
  };

  return (
    <>
      {file.status === 'PROCESSING' ? (
        <TableRow
          sx={{
            backgroundColor: '#F2F4F7',
            borderRadius: '10px',
            overflow: 'hidden',
            '&:last-child td, &:last-child th': { border: 0 },
            boxShadow: 'none',
            textAlign: 'center',
            verticalAlign: 'middle',
            cursor: 'default',
          }}
        >
          <TableCell
            colSpan={8}
            sx={{ textAlign: 'center', borderRadius: '10px' }}
          >
            <Stack
              justifyContent="center"
              alignItems="center"
              spacing={2}
              sx={{ py: 0 }}
            >
              <ReactLoading
                type={'bubbles'}
                color={'#94C729'}
                height={30}
                width={50}
              />
              <Typography
                fontWeight="500"
                fontSize="16px"
                fontFamily="Heebo"
                color="#94C729"
              >
                עיבוד קובץ
              </Typography>
            </Stack>
          </TableCell>
        </TableRow>
      ) : file.status === 'ERROR' ? (
        <TableRow
          sx={{
            backgroundColor: '#FDF2F2',
            borderRadius: '10px',
            overflow: 'hidden',
            '&:last-child td, &:last-child th': { border: 0 },
            boxShadow: 'none',
            textAlign: 'center',
            verticalAlign: 'middle',
            cursor: 'default',
          }}
        >
          <TableCell
            colSpan={8}
            sx={{ textAlign: 'center', borderRadius: '10px' }}
          >
            <Stack
              justifyContent="center"
              alignItems="center"
              spacing={0.5}
              sx={{ py: 0 }}
            >
              <Icon
                icon="mdi:close-circle-outline"
                color="#FF6F78"
                width={40}
                height={40}
              />
              <Typography
                fontWeight="500"
                fontSize="16px"
                fontFamily="Heebo"
                color="#FF6F78"
              >
                הקובץ של {format(new Date(file.date), 'MMMM', { locale: he })}{' '}
                {format(new Date(file.date), 'yyyy', { locale: he })} אינו תואם
                את הפורמט
              </Typography>
            </Stack>
          </TableCell>
        </TableRow>
      ) : (
        <TableRow
          sx={{
            backgroundColor: '#F2F4F7',
            borderRadius: '10px',
            p: '20px',
            overflow: 'hidden',
            '&:last-child td, &:last-child th': { border: 0 },
            boxShadow: 'none',
            textAlign: 'right',
            verticalAlign: 'bottom',
          }}
        >
          <TableCell
            sx={{
              padding: '20px',
              borderBottom: 'none',
              borderTopRightRadius: '10px',
              borderBottomRightRadius: '10px',
            }}
          >
            <Box
              sx={{
                textAlign: 'right',
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'flex-end',
                gap: '2px',
              }}
            >
              <Typography
                fontSize="16px"
                fontWeight="700"
                fontFamily={'AlmoniDL'}
              >
                {format(new Date(file.createdAt), 'MMMM', { locale: he })}
              </Typography>
              <Typography
                fontWeight={'400'}
                fontSize={'12px'}
                fontFamily={'Montserrat'}
              >
                {format(new Date(file.createdAt), 'dd/MM/yyyy', { locale: he })}
              </Typography>
            </Box>
          </TableCell>

          <TableCell
            sx={{
              padding: '20px',
              borderBottom: 'none',
              textAlign: 'right',
              verticalAlign: 'bottom',
            }}
          >
            <Typography
              fontWeight={'700'}
              fontSize={'14px'}
              fontFamily={'AlmoniDL'}
            >
              {file?.senderName}
            </Typography>
          </TableCell>

          <TableCell
            sx={{
              padding: '20px',
              borderBottom: 'none',
              textAlign: 'right',
              verticalAlign: 'bottom',
            }}
          >
            <FundLogo width="100px" fundNumber="8700" />
          </TableCell>

          <TableCell
            sx={{
              padding: '20px',
              borderBottom: 'none',
              textAlign: 'right',
              verticalAlign: 'bottom',
            }}
          >
            <Typography
              fontWeight={'700'}
              fontSize={'14px'}
              fontFamily={'AlmoniDL'}
            >
              קופת גמל להשקעה
            </Typography>
          </TableCell>

          <TableCell
            sx={{
              padding: '20px',
              borderBottom: 'none',
              textAlign: 'right',
              verticalAlign: 'bottom',
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'flex-end', gap: '8px' }}>
              <img src={'/assets/dashboard/coins.svg'} alt="coins" />
              <Typography
                fontWeight={'500'}
                fontSize={'20px'}
                fontFamily={'Montserrat'}
              >
                {totalToBePaid.toLocaleString()}
              </Typography>
            </Box>
          </TableCell>

          <TableCell
            sx={{
              padding: '20px',
              borderBottom: 'none',
              textAlign: 'right',
              verticalAlign: 'bottom',
            }}
          >
            <StatusTextCircle status={file.status || 'PENDING'} />
          </TableCell>

          <TableCell
            sx={{
              py: '20px',
              borderBottom: 'none',
              textAlign: 'left',
              borderTopLeftRadius: '10px',
              borderBottomLeftRadius: '10px',
            }}
          >
            <Stack
              direction="row"
              alignItems="center"
              justifyContent="flex-end"
              spacing={1}
              gap={1}
              sx={{ width: '100%' }}
            >
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'row',
                  alignItems: 'center',
                  gap: '8px',
                }}
              >
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    width: '45px',
                    height: '45px',
                    background: 'white',
                    borderRadius: '10px',
                    cursor: 'pointer',
                  }}
                  onClick={() => {
                    setOpenDetails(true);
                  }}
                >
                  <ArrowIcon />
                </Box>
              </Box>
            </Stack>
          </TableCell>
        </TableRow>
      )}
      <Sidebar
        open={openDetails}
        toggleSidebar={() => {
          setOpenDetails(!openDetails);
        }}
        paymentId={file.id}
      />
    </>
  );
};

export default FileItem;
